// API client for checkout operations

const API_BASE = '/v1';

// =====================================================
// Types
// =====================================================

interface ApiResponse<T = Record<string, unknown>> {
  data?: T;
  error?: {
    code: string;
    message: string;
    retryable: boolean;
  };
}

export interface PostalCodeResponse {
  valid: boolean;
  city?: string;
}

export interface SavedAddressesResponse {
  addresses: SavedAddress[];
}

export interface SavedAddress {
  id: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  countryCode?: string;
  isDefault?: boolean;
}

export interface ShippingOptionsResponse {
  options: ShippingOption[];
}

export interface ShippingOption {
  id: string;
  name: string;
  description: string;
  price: { amount: number; currency: string };
  estimatedDelivery: string;
  provider: string;
}

export interface PaymentMethodsResponse {
  paymentMethods: PaymentMethodInfo[];
  quickPayAvailable: boolean;
  lastUsedMethod?: string;
  // Surfboard SDK data for payment initialization
  surfboardOrderId?: string;
  surfboardNonce?: string;
  publicKey?: string;
}

export interface PaymentMethodInfo {
  id: string;
  name: string;
  icon: string;
  available: boolean;
}

export interface StoreDetailsResponse {
  status: string;
  data: {
    storeId: string;
    merchantId: string;
    name: string;
    status: string;
    onlineOnboardingStatus: string;
    address?: {
      careOf?: string | null;
      addressLine1?: string;
      addressLine2?: string;
      addressLine3?: string;
      city?: string;
      countryCode?: string;
      postalCode?: string;
    };
    onlineInfo?: {
      merchantWebshopURL?: string;
      termsAndConditionsURL?: string;
      privacyPolicyURL?: string;
    };
    phone?: string;
    email?: string;
  };
  message: string;
}

export interface PaymentInitiateResponse {
  redirectUrl?: string;
  paymentId: string;
  status: string;
}

// =====================================================
// Fetch wrapper with error handling
// =====================================================

async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return { error: data.error || { code: 'UNKNOWN', message: 'An error occurred', retryable: false } };
    }
    
    return { data };
  } catch (error) {
    console.error('API error:', error);
    return {
      error: {
        code: 'NETWORK_ERROR',
        message: 'Kunne ikke koble til serveren. Prøv igjen.',
        retryable: true,
      },
    };
  }
}

// =====================================================
// Session API
// =====================================================

export async function getSession(sessionId: string) {
  return apiFetch(`/sessions/${sessionId}`);
}

export async function updateSession(sessionId: string, data: {
  customer?: { email: string; name: string; phone?: string };
  shippingAddress?: { streetAddress: string; postalCode: string; city: string; countryCode?: string };
  billingAddress?: { streetAddress: string; postalCode: string; city: string; countryCode?: string };
}) {
  return apiFetch(`/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// =====================================================
// Shipping API
// =====================================================

export async function getShippingOptions(sessionId: string, postalCode: string): Promise<ApiResponse<ShippingOptionsResponse>> {
  return apiFetch<ShippingOptionsResponse>('/shipping/options', {
    method: 'POST',
    body: JSON.stringify({ sessionId, postalCode }),
  });
}

export async function selectShipping(sessionId: string, shippingOptionId: string) {
  return apiFetch('/shipping/select', {
    method: 'POST',
    body: JSON.stringify({ sessionId, shippingOptionId }),
  });
}

export async function getPickupPoints(postalCode: string, provider?: string) {
  return apiFetch('/shipping/pickup-points', {
    method: 'POST',
    body: JSON.stringify({ postalCode, countryCode: 'NO', provider }),
  });
}

// =====================================================
// Payment API
// =====================================================

export async function getPaymentMethods(sessionId: string, deviceToken?: string | null): Promise<ApiResponse<PaymentMethodsResponse>> {
  return apiFetch<PaymentMethodsResponse>(`/payment/methods/${sessionId}`, {
    headers: deviceToken ? { 'X-Device-Token': deviceToken } : {},
  });
}

export async function initiatePayment(sessionId: string, paymentMethod: string): Promise<ApiResponse<PaymentInitiateResponse>> {
  return apiFetch<PaymentInitiateResponse>('/payment/initiate', {
    method: 'POST',
    body: JSON.stringify({ sessionId, paymentMethod }),
  });
}

export async function executeQuickPay(sessionId: string, deviceToken: string): Promise<ApiResponse<PaymentInitiateResponse>> {
  return apiFetch<PaymentInitiateResponse>('/payment/quick-pay', {
    method: 'POST',
    body: JSON.stringify({ sessionId, deviceToken }),
  });
}

export async function verifyPayment(sessionId: string) {
  return apiFetch('/payment/verify', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });
}

export async function savePaymentPreference(sessionId: string, paymentMethod: string, saveForNetwork = false) {
  return apiFetch('/payment/save-preference', {
    method: 'POST',
    body: JSON.stringify({ sessionId, paymentMethod, saveForNetwork }),
  });
}

// =====================================================
// Preferences API
// =====================================================

export async function lookupPreference(email: string, postalCode: string, merchantId: string) {
  return apiFetch('/preferences/lookup', {
    method: 'POST',
    body: JSON.stringify({ email, postalCode, merchantId }),
  });
}

export async function getSavedAddresses(email: string): Promise<ApiResponse<SavedAddressesResponse>> {
  return apiFetch<SavedAddressesResponse>('/preferences/addresses', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function saveAddress(email: string, address: {
  streetAddress: string;
  postalCode: string;
  city: string;
  countryCode?: string;
}, isDefault = false, consent = true) {
  return apiFetch('/preferences/addresses/save', {
    method: 'POST',
    body: JSON.stringify({ email, address, isDefault, consent }),
  });
}

export async function lookupPostalCode(postalCode: string): Promise<ApiResponse<PostalCodeResponse>> {
  return apiFetch<PostalCodeResponse>(`/preferences/postnummer/${postalCode}`);
}

// =====================================================
// Store/Merchant API
// =====================================================

export async function getStoreDetails(
  merchantId: string
): Promise<ApiResponse<StoreDetailsResponse>> {
  return apiFetch<StoreDetailsResponse>(`/store/${merchantId}/details`);
}

// Legacy endpoint with explicit partnerId/storeId
export async function getStoreDetailsLegacy(
  partnerId: string, 
  merchantId: string, 
  storeId: string
): Promise<ApiResponse<StoreDetailsResponse>> {
  return apiFetch<StoreDetailsResponse>(`/store/${partnerId}/${merchantId}/${storeId}`);
}

export interface MerchantPaymentMethodsResponse {
  status: string;
  data: Array<{
    paymentMethodId?: string;
    paymentMethod: string;
  }>;
  cached?: boolean;
  fallback?: boolean;
}

export async function getMerchantPaymentMethods(
  merchantId: string
): Promise<ApiResponse<MerchantPaymentMethodsResponse>> {
  return apiFetch<MerchantPaymentMethodsResponse>(`/store/${merchantId}/payment-methods`);
}

// =====================================================
// Polling utility for payment verification
// =====================================================

export function pollPaymentStatus(
  sessionId: string,
  onSuccess: (data: any) => void,
  onError: (error: any) => void,
  maxAttempts = 30,
  intervalMs = 2000
) {
  let attempts = 0;
  
  const poll = async () => {
    attempts++;
    
    const result = await verifyPayment(sessionId);
    
    if (result.error) {
      if (result.error.retryable && attempts < maxAttempts) {
        setTimeout(poll, intervalMs);
      } else {
        onError(result.error);
      }
      return;
    }
    
    const data = result.data as any;
    
    if (data.isComplete) {
      onSuccess(data);
    } else if (data.paymentStatus === 'PAYMENT_CANCELLED') {
      onError({ code: 'PAYMENT_CANCELLED', message: 'Betalingen ble avbrutt' });
    } else if (attempts < maxAttempts) {
      // Exponential backoff
      const nextInterval = Math.min(intervalMs * Math.pow(1.2, attempts - 1), 10000);
      setTimeout(poll, nextInterval);
    } else {
      onError({ code: 'TIMEOUT', message: 'Tidsavbrudd. Prøv igjen.' });
    }
  };
  
  poll();
  
  // Return cancel function
  return () => {
    attempts = maxAttempts;
  };
}

// =====================================================
// Saved Card Tokens API
// =====================================================

export interface SavedToken {
  tokenId: string;
  cardBrand: string;
  cardholderName: string;
  truncatedPan: string;
  expiryMonth: number;
  expiryYear: number;
  cardArt?: string;
  displayName: string;
}

export interface TokensResponse {
  tokens: SavedToken[];
}

export interface MITPaymentResponse {
  paymentId: string;
  status: string;
}

/**
 * Get saved card tokens for a customer in a session
 */
export async function getSavedTokens(sessionId: string): Promise<ApiResponse<TokensResponse>> {
  return apiFetch<TokensResponse>(`/payment/tokens/${sessionId}`);
}

/**
 * Delete a saved card token
 */
export async function deleteToken(tokenId: string, sessionId: string): Promise<ApiResponse<{ deleted: boolean }>> {
  return apiFetch<{ deleted: boolean }>(`/payment/tokens/${tokenId}?sessionId=${sessionId}`, {
    method: 'DELETE',
  });
}

/**
 * Initiate a Merchant Initiated Transaction using a saved token
 */
export async function initiateMITPayment(
  sessionId: string,
  tokenId: string,
  paymentType: 'MIT_UNSCHEDULED' | 'MIT_RECURRING' = 'MIT_UNSCHEDULED'
): Promise<ApiResponse<MITPaymentResponse>> {
  return apiFetch<MITPaymentResponse>('/payment/mit', {
    method: 'POST',
    body: JSON.stringify({ sessionId, tokenId, paymentType }),
  });
}
